import { useNavigate } from 'react-router-dom'
import { DataTable, Button, Skeleton } from '@rb/ui'
import type { DataTableColumn } from '@rb/ui'
import { useFetch } from '../hooks/useFetch'
import styles from './AdminActivity.module.css'

interface ActivityEntry {
  id: string
  project_id: string | null
  actor_type: string
  action: string
  details: Record<string, unknown> | null
  created_at: string
  project_name: string | null
  user_email: string | null
  user_name: string | null
  client_email: string | null
}

interface ActivityResponse {
  activity: ActivityEntry[]
}

const ACTION_LABELS: Record<string, string> = {
  status_changed: 'Status geändert',
  file_issue_flagged: 'Datei-Issue gemeldet',
  file_issue_cleared: 'Datei-Issue gelöscht',
  analysis_uploaded: 'Analyseergebnisse hochgeladen',
  analysis_published: 'Ergebnisse veröffentlicht',
}

const ACTOR_LABELS: Record<string, string> = {
  admin: 'Admin',
  client: 'Kunde',
  system: 'System',
  symon: 'Symon',
}

function formatDateTime(s: string) {
  const d = new Date(s)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleString('de-AT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function AdminActivity() {
  const navigate = useNavigate()
  const { data, loading, error, reload } = useFetch<ActivityResponse>('/admin/activity')
  const rows = data?.activity ?? []

  const columns: DataTableColumn<ActivityEntry>[] = [
    {
      key: 'created_at',
      header: 'Zeitpunkt',
      render: (row) => <span className={styles.date}>{formatDateTime(row.created_at)}</span>,
      width: '160px',
    },
    {
      key: 'actor',
      header: 'Wer',
      render: (row) => (
        <div>
          <div className={styles.actorType}>{ACTOR_LABELS[row.actor_type] ?? row.actor_type}</div>
          {(row.user_name || row.user_email) && (
            <div className={styles.actorEmail}>{row.user_name ?? row.user_email}</div>
          )}
        </div>
      ),
      width: '160px',
    },
    {
      key: 'action',
      header: 'Aktion',
      render: (row) => (
        <span className={styles.action}>{ACTION_LABELS[row.action] ?? row.action}</span>
      ),
      width: '220px',
    },
    {
      key: 'project',
      header: 'Projekt / Kunde',
      render: (row) => (
        <div>
          <div className={styles.projectName}>{row.project_name ?? '—'}</div>
          {row.client_email && (
            <div className={styles.clientEmail}>{row.client_email}</div>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.heading}>Aktivitätslog</h1>
      </header>

      {loading && (
        <div className={styles.skeletons}>
          <Skeleton className={styles.skeletonRow} />
          <Skeleton className={styles.skeletonRow} />
          <Skeleton className={styles.skeletonRow} />
          <Skeleton className={styles.skeletonRow} />
        </div>
      )}

      {!loading && error && (
        <div className={styles.errorState}>
          <p>Fehler beim Laden</p>
          <Button variant="secondary" onClick={reload}>Erneut versuchen</Button>
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className={styles.empty}>Noch keine Aktivitäten.</div>
      )}

      {!loading && !error && rows.length > 0 && (
        <DataTable
          columns={columns}
          rows={rows}
          getRowKey={(row) => row.id}
          onRowClick={(row) =>
            row.project_id ? navigate(`/admin/projects/${row.project_id}`) : undefined
          }
        />
      )}
    </div>
  )
}
